import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Bot, Brush, Code2, Gauge, Layers3, Megaphone, ShieldCheck, Timer, Workflow } from 'lucide-react';
import ServiceLeadForm from './ServiceLeadForm';

const podCards = [
  {
    title: 'Automation + Systems Pod',
    icon: Bot,
    outcome: 'Senior PM-led automation implementation that removes repetitive delivery load.',
    speed: 'Turnkey activation in < 7 minutes',
  },
  {
    title: 'Creative + Content Pod',
    icon: Brush,
    outcome: 'Add immediate creative throughput with AI-leveraged specialists and PM quality control.',
    speed: 'Daily managed production',
  },
  {
    title: 'Development Pod',
    icon: Code2,
    outcome: 'Launch technical delivery with a turnkey PM + development team built for agency execution.',
    speed: 'Sprint-ready managed delivery',
  },
  {
    title: 'Marketing Ops Pod',
    icon: Megaphone,
    outcome: 'Scale campaign and reporting operations with PM-owned execution and AI-assisted workflows.',
    speed: 'Cross-account managed orchestration',
  },
];

const faqs = [
  {
    q: 'Is this white-label?',
    a: 'Yes. Pods operate as a white-label execution layer inside your agency brand and client workflow.',
  },
  {
    q: 'How fast is onboarding?',
    a: 'Most agencies are activated the same day, with pod deployment beginning in minutes after intake.',
  },
  {
    q: 'Will this work with our stack?',
    a: 'Pods are designed to plug into your existing tools, delivery systems, and client communication rhythm.',
  },
  {
    q: 'Is this offshore freelancer staffing?',
    a: 'No. This is a turnkey delivery team model: senior Project Manager + AI-leveraged resources with clear accountability.',
  },
  {
    q: 'Can we scale up or down?',
    a: 'Yes. Pod capacity expands or contracts based on current workload and account demand.',
  },
  {
    q: 'Do we need long retainers?',
    a: 'Engagement is flexible and aligned to delivery needs, not rigid headcount-style commitments.',
  },
];

const ctaHref = '#pod-lead-form';

const HirePodLanding = () => {
  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100">
      <Helmet>
        <title>Hire a POD | HireX8</title>
        <meta
          name="description"
          content="Turnkey Project Manager + Resources for agencies. Add capabilities fast with senior PM-led, AI-leveraged execution."
        />
      </Helmet>

      <div className="sticky top-0 z-40 backdrop-blur bg-[#070b14]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-sm font-semibold text-orange-400 hover:opacity-80">
            ← Back to HireX8
          </Link>
          <a href={ctaHref} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-black hover:bg-orange-400 transition-colors">
            Book A Strategy Call <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <section className="px-6 pt-14 sm:pt-20 pb-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">Turnkey agency execution infrastructure</p>
            <h1 className="text-4xl sm:text-6xl font-black leading-[0.95] tracking-tight text-white">
              Turnkey Delivery Capacity For Agencies Of Any Size
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Hire a POD gives you a senior Project Manager plus AI-leveraged resources to execute high-quality client work
              without adding internal hiring overhead.
            </p>
            <p className="mt-4 text-slate-400 font-medium">No recruiting. No ramp-up drag. Execution guaranteed through PM-led delivery.</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={ctaHref} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold hover:bg-orange-400 transition-colors">
                Book A Strategy Call <ArrowRight size={16} />
              </a>
              <a href="#pods" className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/20 text-slate-100 font-semibold hover:bg-white/5 transition-colors">
                See Available Pods
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              {['Project Manager + Resources', '7-minute activation', 'White-label delivery', 'AI-leveraged talent', 'Flexible engagement'].map((m) => (
                <span key={m} className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300 bg-white/5">
                  {m}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-semibold text-slate-200">Turnkey Delivery Orchestration</p>
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300">Live</span>
            </div>
            <div className="space-y-3">
              {[
                ['Pod Intake', 'Completed'],
                ['Senior PM Assignment', 'In Progress'],
                ['Resource Matching', 'Queued'],
                ['Execution Stream', 'Ready'],
              ].map(([item, status], idx) => (
                <div key={item} className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-200">{item}</span>
                    <span className="text-slate-400">{status}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-orange-400"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${Math.min(30 + (idx + 1) * 18, 95)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: idx * 0.12 }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              {[
                { icon: Timer, label: '7m', sub: 'Activation' },
                { icon: Workflow, label: 'PM', sub: 'Managed' },
                { icon: Layers3, label: 'AI', sub: 'Leverage' },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={sub} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                  <Icon size={15} className="mx-auto text-orange-400 mb-1" />
                  <div className="font-bold text-white">{label}</div>
                  <div className="text-[11px] text-slate-400">{sub}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            Your Agency Doesn’t Need More Hiring. It Needs Turnkey Leverage.
          </h2>
          <p className="mt-4 text-slate-300 max-w-3xl">
            When demand spikes, most agencies get blocked by hiring delays and management load. A turnkey Project Manager
            plus resources adds immediate capability and protects delivery quality.
          </p>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-6">
              <h3 className="font-bold text-red-200 mb-3">Traditional Hiring</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>Weeks to months before contribution</li>
                <li>High recruiting, onboarding, and PM overhead</li>
                <li>Fixed payroll risk regardless of demand</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-6">
              <h3 className="font-bold text-emerald-200 mb-3">HireX8 Pods</h3>
              <ul className="space-y-2 text-slate-200 text-sm">
                <li>Turnkey PM + resources activated in minutes</li>
                <li>Senior PM-led execution with AI-leveraged talent</li>
                <li>Flexible capability aligned to active client load</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="pods" className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Specialized Turnkey Pods Built For Agencies</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {podCards.map(({ title, icon: Icon, outcome, speed }) => (
              <motion.div
                key={title}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-orange-300/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4">
                  <Icon size={18} className="text-orange-300" />
                </div>
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <p className="mt-3 text-slate-300">{outcome}</p>
                <p className="mt-4 text-sm text-orange-300 font-semibold">{speed}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Hiring Adds Overhead. Pods Add Output.</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
            <div className="grid grid-cols-3 bg-white/[0.05] text-xs uppercase tracking-wider font-semibold">
              <div className="p-4 text-slate-300">Dimension</div>
              <div className="p-4 text-slate-400 border-l border-white/10">Traditional Hiring</div>
              <div className="p-4 text-orange-300 border-l border-white/10">HireX8 Pods</div>
            </div>
            {[
              ['Activation speed', '4-12 weeks', 'Under 7 minutes'],
              ['Execution ownership', 'Internal PM burden', 'Senior PM-led delivery'],
              ['Scalability', 'Slow and fixed', 'Elastic and demand-aligned'],
              ['Capability model', 'Role-by-role hiring', 'Turnkey PM + resources'],
              ['AI leverage', 'Manual adoption effort', 'AI-leveraged talent by default'],
            ].map((row) => (
              <div key={row[0]} className="grid grid-cols-3 border-t border-white/10 text-sm">
                <div className="p-4 text-slate-200">{row[0]}</div>
                <div className="p-4 text-slate-400 border-l border-white/10">{row[1]}</div>
                <div className="p-4 text-slate-100 border-l border-white/10">{row[2]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">From Request To Execution In 7 Minutes</h2>
          <div className="mt-8 grid md:grid-cols-4 gap-4">
            {[
              'Tell us what needs to get done',
              'We assign a senior Project Manager',
              'We deploy matched AI-leveraged resources',
              'You deliver more without adding headcount',
            ].map((step, idx) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="text-xs text-orange-300 font-semibold mb-3">Step {idx + 1}</div>
                <p className="text-slate-200">{step}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Execution You Can Trust, Managed By Senior PMs</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {[
              'Reduced turnaround time by 61%',
              'Replaced multiple internal hires with one turnkey pod',
              'Scaled technical delivery across multi-account agency portfolios',
            ].map((m) => (
              <div key={m} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <Gauge size={18} className="text-orange-300 mb-3" />
                <p className="text-lg text-slate-100">{m}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-slate-400 text-sm">
            Logo rail, case studies, and deeper references can be added here as we publish approved assets.
          </div>
        </div>
      </section>

      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">Questions, Answered</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {faqs.map((item) => (
              <details key={item.q} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 group">
                <summary className="cursor-pointer list-none flex items-center justify-between text-slate-100 font-semibold">
                  {item.q}
                  <ShieldCheck size={16} className="text-orange-300 group-open:rotate-6 transition-transform" />
                </summary>
                <p className="mt-3 text-slate-300 text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-7xl mx-auto rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white max-w-4xl">
            Stop Turning Down Work Because Your Team Is Maxed Out
          </h2>
          <p className="mt-4 text-lg text-slate-300 max-w-3xl">
            Add turnkey Project Manager + Resources capacity that expands what your agency can deliver immediately.
          </p>
          <p className="mt-2 text-slate-400">No recruiting. No onboarding drag. PM-led execution with AI-leveraged talent.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={ctaHref} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold hover:bg-orange-400 transition-colors">
              Book A Strategy Call <ArrowRight size={16} />
            </a>
            <a href="#pods" className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/20 text-slate-100 font-semibold hover:bg-white/5 transition-colors">
              See Available Pods
            </a>
          </div>
        </div>
      </section>

      <section id="pod-lead-form" className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <ServiceLeadForm
            serviceName="Hire a POD"
            submitLabel="Book A Strategy Call"
            apiEndpoint="/api/send-lead"
            successRedirectPath="/pod/what-to-expect"
          />
        </div>
      </section>

      <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 p-3 bg-[#070b14]/95 border-t border-white/10 backdrop-blur">
        <a href={ctaHref} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-black font-semibold">
          Book A Strategy Call <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
};

export default HirePodLanding;
